import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  Users, 
  Target, 
  Award, 
  CheckCircle, 
  ArrowRight,
  Mail,
  Phone
} from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import corporateBackground from "@/assets/corporate-background.jpg";

const services = [
  {
    icon: Users,
    title: "Team Training Programs",
    description: "Customized training sessions designed for your team's specific needs and skill levels."
  },
  {
    icon: Target,
    title: "Project Management Consulting",
    description: "Expert guidance on implementing best practices and optimizing your project workflows."
  },
  {
    icon: Building2,
    title: "Digital Transformation",
    description: "End-to-end support for adopting Microsoft Power Platform and modern automation tools."
  },
  {
    icon: Award,
    title: "Certification Programs",
    description: "Prepare your team for industry-recognized certifications with structured learning paths."
  }
];

const packages = [
  {
    name: "Starter",
    description: "Perfect for small teams",
    features: [
      "Up to 10 participants",
      "2 training sessions",
      "Basic templates library",
      "Email support"
    ],
    highlighted: false
  },
  {
    name: "Professional",
    description: "Most popular for growing teams",
    features: [
      "Up to 30 participants",
      "6 training sessions",
      "Full templates library",
      "Priority support",
      "Custom workshops",
      "Progress tracking"
    ],
    highlighted: true
  },
  {
    name: "Enterprise",
    description: "For large organizations",
    features: [
      "Unlimited participants",
      "Unlimited sessions",
      "Custom curriculum",
      "Dedicated account manager",
      "On-site training options",
      "API integrations"
    ],
    highlighted: false
  }
];

const Corporate = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Inquiry Submitted",
      description: "We'll get back to you within 24 hours."
    });
    setFormData({ name: "", email: "", company: "", message: "" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center">
          {/* Full-size Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${corporateBackground})` }}
          >
            <div className="absolute inset-0 bg-black/50" />
          </div>

          {/* Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center w-full">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">CORPORATE SOLUTIONS</Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Empower Your Team. <span className="text-white/80">Transform Your Business.</span>
            </h1>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Custom training programs and consulting services designed to accelerate your organization's digital transformation journey.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="#contact">
                <Button size="lg" className="bg-white text-foreground hover:bg-white/90">
                  Get in Touch
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a href="#packages">
                <Button variant="outline" size="lg" className="bg-white/10 border-white/50 text-white hover:bg-white/20">
                  View Packages
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Our Corporate Services
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Comprehensive solutions tailored to your organization's unique needs.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{service.title}</h3>
                  <p className="text-sm text-muted-foreground">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages Section */}
        <section id="packages" className="py-16 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Training Packages
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Choose the package that best fits your team size and training goals.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {packages.map((pkg, index) => (
                <div 
                  key={index}
                  className={`bg-card rounded-lg p-6 ${
                    pkg.highlighted 
                      ? "border-2 border-primary shadow-lg" 
                      : "border border-border"
                  }`}
                >
                  {pkg.highlighted && (
                    <Badge className="mb-4 bg-primary text-primary-foreground">MOST POPULAR</Badge>
                  )}
                  <h3 className="text-xl font-bold text-foreground mb-1">{pkg.name}</h3>
                  <p className="text-sm text-muted-foreground mb-6">{pkg.description}</p>
                  
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a href="#contact">
                    <Button 
                      className="w-full" 
                      variant={pkg.highlighted ? "default" : "outline"}
                    >
                      Contact Sales
                    </Button>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                  Let's Discuss Your Training Needs
                </h2>
                <p className="text-muted-foreground mb-8">
                  Fill out the form and our team will get back to you within 24 hours to discuss how we can help your organization succeed.
                </p>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium text-foreground">info@fts-it-ksa.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium text-foreground">+20 1012345293</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">
                      Full Name
                    </label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">
                      Work Email
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">
                      Company Name
                    </label>
                    <Input
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Acme Inc."
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">
                      How can we help?
                    </label>
                    <Textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your training needs..."
                      rows={4}
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    Submit Inquiry
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Corporate;
